import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useConfirm, useToast } from './feedback.jsx';
import { t } from '../i18n/index.jsx';

/**
 * Link/unlink the current user's Telegram account for read-mostly bot
 * commands (/due, /balance, /project, /approve). The code is generated
 * server-side and expires in 10 minutes; the user sends it to the bot as
 * "/start CODE" to complete the link.
 */
export default function TelegramLink() {
  const toast = useToast();
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try { setStatus(await api.get('/telegram/status')); } catch { setStatus({ linked: false, configured: false }); }
  }
  useEffect(() => { load(); }, []);

  async function generateCode() {
    setError(''); setBusy(true);
    try {
      const r = await api.post('/telegram/link-code');
      setPending(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function unlink() {
    if (!(await confirm({ title: t('Unlink Telegram'), message: t('Stop this Telegram account from receiving bot replies and reminders?'), confirmLabel: t('Unlink'), danger: true }))) return;
    setBusy(true);
    try {
      await api.post('/telegram/unlink');
      setStatus((s) => ({ ...s, linked: false }));
      toast(t('Telegram unlinked'));
    } catch (e) { toast(e.message, { tone: 'error' }); }
    finally { setBusy(false); }
  }

  if (!status || !status.configured) return null;

  return (
    <section className="panel" style={{ maxWidth: 460 }}>
      <h2 className="panel-title">{t('Telegram')}</h2>
      {error && <div className="alert error">{error}</div>}

      {status.linked ? (
        <>
          <p className="muted sm">{t('Your Telegram account is linked. Message the bot with /help to see available commands.')}</p>
          <button className="btn danger ghost sm" onClick={unlink} disabled={busy}>{t('Unlink Telegram')}</button>
        </>
      ) : pending ? (
        <>
          <p className="muted sm">
            {t('Open Telegram, message')} {pending.bot_username ? <b>@{pending.bot_username}</b> : t('the bot')}, {t('and send:')}
          </p>
          <p style={{ fontFamily: 'monospace', fontSize: 16, background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 8 }}>
            /start {pending.code}
          </p>
          <p className="muted sm">{t('This code expires in {n} minutes.', { n: pending.expires_in_minutes })}</p>
          <button className="btn ghost sm" onClick={() => setPending(null)}>{t('Close')}</button>
        </>
      ) : (
        <>
          <p className="muted sm">{t('Link Telegram to get due-date and balance lookups from your phone (/due, /balance, /project). Read-only — no changes can be made via chat.')}</p>
          <button className="btn primary sm" onClick={generateCode} disabled={busy}>{t('Generate link code')}</button>
        </>
      )}
    </section>
  );
}
